import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { Button, CircularProgress } from "@mui/material";
import { Download, Delete, CloudUpload } from "@mui/icons-material";
import BASE_URL from "../../config/apiConfig";
import { checkRole, generateDownload } from "../../util/FormUtils";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import DoneIcon from "@mui/icons-material/Done";
import DangerousIcon from "@mui/icons-material/Dangerous";
import DescriptionIcon from "@mui/icons-material/Description";
import { showToast, showErrorToast } from "../../util/showToast";
import { UserContext } from "../../context/UserContext";

const BoxFileManager = ({ folderId, title, allowedRoles }) => {
    const { user } = useContext(UserContext);
    const canUpload = checkRole(user, allowedRoles);
    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    const fetchFiles = async () => {
        try {
            const response = await axios.get(BASE_URL + `/api/box/folders/${folderId}`, { withCredentials : true });
            setFiles(response.data.items);
        } catch (error) {
            console.error("Error fetching files: ", error);
            setFiles([]);
        }
    };

    useEffect(() => {
        fetchFiles().then();
    }, []);

    const handleDownload = async (fileId) => {
        showToast("Downloading file...", <Download style={{ color: "#2155bc" }} />);

        axios.get(BASE_URL + `/api/box/download/${fileId}`, { responseType: "blob", withCredentials: true })
            .then((response) => { generateDownload(response) })
            .catch((error) => { showErrorToast(`File download failed: ${error}`, <DangerousIcon style={{ color: "#ef4337" }} />) });
    };

    const handleDelete = async (fileId) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this file?");
        showToast("Deleting file...", <Delete style={{ color: "#2155bc" }} />);

        if (confirmDelete) {
            axios.delete(BASE_URL + `/api/box/delete/${fileId}`, { withCredentials : true })
                .then(() => {setFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileId));
            }).catch((error) => {
                console.error("Error deleting file: ", error);
                showErrorToast(`File delete failed: ${error}`, <DangerousIcon style={{ color: "#ef4337" }} />);
            });
        }
    };

    const handleUpload = async (e) => {
        const selectedFile = e.target.files[0];

        if (selectedFile) {
            setIsUploading(true);
            const formData = new FormData();
            formData.append("file", selectedFile);

            axios.post(BASE_URL + "/api/box/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
                params: { folderId: folderId, fileName: selectedFile.name },
                withCredentials: true,
            }).then((response) => {
                if (!files.find((file) => file.name === selectedFile.name)) {
                    setFiles((prevFiles) => [...prevFiles, response.data]);
                }

                showToast("File has been successfully uploaded.", <DoneIcon style={{ color: "#61c313" }} />);
            }).catch((error) => {
                console.error("Error uploading file: ", error);
                showErrorToast(`File upload failed: ${error}`, <DangerousIcon style={{ color: "#ef4337" }} />);
            }).finally(() => {
                setIsUploading(false);
            });
        }
    };

    return (
        <div id="boxFileManager" className="boxFileManager">
            <div className="boxFileManagerHeader">
                <a
                    href={`https://bri.app.box.com/folder/${folderId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="boxFileManagerHeaderTitle"
                >
                    {title}
                </a>
                <div>
                    <label
                        htmlFor={`${folderId}-upload-input`}
                        className={canUpload ? "abled-label" : "disabled-label"}
                        style={{ display: "flex", alignItems: "center" }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            component="span"
                            disabled={!canUpload}
                            className={canUpload ? "boxFileManagerHeaderUpload" : "disabled-button"}
                        >
                            {isUploading ? (
                                <CircularProgress size={18} style={{ color: "white" }} />
                            ) : (
                                <div className="boxFileManagerHeaderUploadContent">
                                    <CloudUpload />
                                    <span style={{ marginTop: "3px" }}>UPLOAD</span>
                                </div>
                            )}
                        </Button>
                        <input
                            type="file"
                            id={`${folderId}-upload-input`}
                            disabled={!canUpload}
                            style={{ display: "none" }}
                            onChange={handleUpload}
                            onClick={(e) => {
                                e.target.value = null;
                            }}
                        />
                    </label>
                </div>
            </div>
            <div className="boxFileManagerBody">
                {files.filter((file) => file.type === "file").length === 0 ? (
                    <div className="boxFileManagerBodyNone">
                        <FilePresentIcon fontSize="small" />
                        <p>No files found.</p>
                    </div>
                ) : (
                    files.filter((file) => file.type === "file").map((file) => (
                        <div key={file.id} className="boxFileManagerBodyFile">
                            <a
                                href={`https://bri.app.box.com/file/${file.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="boxFileManagerBodyFileTitle"
                            >
                                <DescriptionIcon fontSize="small" />
                                {file.name}
                            </a>
                            <div className="boxFileManagerBodyFileActions">
                                <button onClick={() => handleDownload(file.id)}>
                                    <Download />
                                </button>
                                <button
                                    onClick={() => handleDelete(file.id)}
                                    disabled={!canUpload}
                                >
                                    <Delete />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default BoxFileManager;
